const BASE_URL = process.env.NEXT_PUBLIC_API_URL?.replace(/\/api\/?$/, "/portal/") || "http://localhost:3000/portal/"

const sessionKey = (invitationToken: string) => `slant_intake_form_session:${invitationToken}`
const invitationDraftSaveKey = (token: string, sessionToken: string) => JSON.stringify([token, sessionToken])

export function getPublicIntakeFormToken(invitationToken: string) {
  if (typeof window === "undefined") return null
  try {
    return window.localStorage.getItem(sessionKey(invitationToken))
  } catch {
    return null
  }
}

export function setPublicIntakeFormToken(invitationToken: string, sessionToken: string) {
  try {
    window.localStorage.setItem(sessionKey(invitationToken), sessionToken)
  } catch {
    // The verified session remains available in memory when storage is blocked.
  }
}

export function clearPublicIntakeFormToken(invitationToken: string) {
  try {
    window.localStorage.removeItem(sessionKey(invitationToken))
  } catch {
    // Storage may be unavailable in privacy-restricted browser contexts.
  }
}

export interface PublicIntakeFormPublication {
  form_name: string
  company_name: string
  company_logo_url: string | null
  company_favicon_url: string | null
  status: "open" | "closed"
  version_id: string | null
}

export type PublicIntakeFormInvitationState = "open" | "submitted" | "revoked" | "expired"

export interface PublicIntakeFormInvitation {
  form_name: string
  company_name: string
  company_logo_url: string | null
  state: PublicIntakeFormInvitationState
  recipient_first_name: string
  masked_recipient_email: string
  verification_recipients: Array<{ token: string; masked_email: string }>
  test_mode: boolean
  version_id?: string
  prefill_answers?: Record<string, unknown>
}

export interface PublicIntakeFormSessionToken {
  session_token: string
  expires_at: string
}

export interface PublicIntakeFormUpload {
  id?: string
  signed_id: string
  filename: string
  byte_size: number
  content_type?: string
}

export interface PublicIntakeFormUploadReservation {
  field_key: string
  signed_id: string
  byte_size: number
}

export function fetchPublicIntakeFormPublicationUploadReservations(token: string, clientSubmissionId: string) {
  const query = new URLSearchParams({ client_submission_id: clientSubmissionId })
  return request<PublicIntakeFormUploadReservation[]>(publicationPath(token, `/uploads?${query}`))
}

export interface PublicIntakeFormDraftDocument {
  id: string
  field_key: string
  signed_id: string
  filename: string
  byte_size: number
  content_type: string
}

export interface PublicIntakeFormSubmissionPayload {
  client_submission_id: string
  answers: Record<string, unknown>
  documents: { field_key: string; signed_id: string }[]
  attestations: { field_key: string; typed_name: string }[]
}

export interface PublicIntakeFormDraft {
  id: string
  version_id: string
  client_submission_id: string
  answers: Record<string, unknown>
  sensitive_paths: string[]
  documents: PublicIntakeFormDraftDocument[]
  draft_state: {
    attestations?: Record<string, string>
    touched_keys?: string[]
    nav?: {
      active_step_key?: string | null
      completed?: Record<string, boolean>
      focus_key?: string | null
      focus_started?: boolean
    }
  }
  updated_at: string
}

export interface PublicIntakeFormDraftPayload {
  id: string | null
  client_submission_id: string
  answers: Record<string, unknown>
  documents: { field_key: string; signed_id: string }[]
  attestations: Record<string, string>
  touched_keys: string[]
  nav: {
    active_step_key: string | null
    completed: Record<string, boolean>
    focus_key?: string | null
    focus_started?: boolean
  }
}

type InvitationDraftSave = () => Promise<PublicIntakeFormDraft>

interface InvitationDraftRequestState {
  revision: number
  saveTail: Promise<void>
  pendingSaveCount: number
  activeReadCount: number
  failedSaves: InvitationDraftSave[]
}

const invitationDraftRequests = new Map<string, InvitationDraftRequestState>()

function draftRequestState(key: string) {
  const existing = invitationDraftRequests.get(key)
  if (existing) return existing

  const state: InvitationDraftRequestState = {
    revision: 0,
    saveTail: Promise.resolve(),
    pendingSaveCount: 0,
    activeReadCount: 0,
    failedSaves: [],
  }
  invitationDraftRequests.set(key, state)
  return state
}

function clearDraftRequestState(key: string, state: InvitationDraftRequestState) {
  if (
    invitationDraftRequests.get(key) === state &&
    state.pendingSaveCount === 0 &&
    state.activeReadCount === 0 &&
    state.failedSaves.length === 0
  ) {
    invitationDraftRequests.delete(key)
  }
}

function enqueueDraftOperation<T>(
  key: string,
  state: InvitationDraftRequestState,
  operation: () => Promise<T>,
  failedSave?: InvitationDraftSave
) {
  state.revision += 1
  state.pendingSaveCount += 1

  const result = state.saveTail.then(async () => {
    try {
      while (state.failedSaves.length > 0) {
        await state.failedSaves[0]()
        state.failedSaves.shift()
      }
      return await operation()
    } catch (error) {
      if (failedSave) state.failedSaves.push(failedSave)
      throw error
    }
  })
  const settled = result.then(
    () => undefined,
    () => undefined
  )
  state.saveTail = settled
  void settled.then(() => {
    state.pendingSaveCount -= 1
    clearDraftRequestState(key, state)
  })
  return result
}

// 201 returns the submission id; 202 means a scheduling booking is confirming
// and the shell must poll the attempt until it resolves.
export type PublicIntakeFormSubmissionResult = { id: string } | { status: "confirming"; attempt_id: string }

export interface PublicIntakeFormSchedulingAvailability {
  available?: boolean
  start_times?: string[]
  slots?: {
    start_time: string
    locations: { id: string; label: string; type: "physical" | "phone" | "video" }[]
  }[]
}

export interface PublicIntakeFormBookingAttemptStatus {
  status: "succeeded" | "failed" | "confirming" | "action_required"
  submission_id?: string
  booking_confirmed?: boolean
  errors?: string[]
  field_errors?: Record<string, string>
}

export class PublicIntakeFormError extends Error {
  public status: number
  public errors: string[]
  public fieldErrors: Record<string, string>
  public expiredUploadSignedIds: string[]

  constructor(
    status: number,
    message: string,
    errors: string[] = [],
    fieldErrors: Record<string, string> = {},
    expiredUploadSignedIds: string[] = []
  ) {
    super(message)
    this.name = "PublicIntakeFormError"
    this.status = status
    this.errors = errors
    this.fieldErrors = fieldErrors
    this.expiredUploadSignedIds = expiredUploadSignedIds
  }
}

export function publicIntakeFormUploadErrorMessage(error: unknown, fieldKey: string) {
  if (error instanceof PublicIntakeFormError && error.status === 422) {
    const message = error.errors.join(". ") || error.message
    const prefix = `${fieldKey}: `
    return message.startsWith(prefix) ? message.slice(prefix.length) : message
  }
  return "Upload failed. Please try again later."
}

interface PortalRequestOptions {
  method?: string
  json?: unknown
  formData?: FormData
  sessionToken?: string | null
  responseMode?: "json" | "text"
  signal?: AbortSignal
}

interface PortalErrorBody {
  error?: string
  errors?: string[]
  field_errors?: Record<string, string>
  expired_upload_signed_ids?: string[]
}

async function buildError(response: Response) {
  let body: PortalErrorBody = {}
  try {
    body = (await response.json()) as PortalErrorBody
  } catch {
    // The portal may answer with a non-JSON error page. Keep the friendly fallback.
  }

  const message = body.error || body.errors?.[0] || "Something went wrong. Please try again."
  return new PublicIntakeFormError(
    response.status,
    message,
    body.errors ?? [],
    body.field_errors ?? {},
    body.expired_upload_signed_ids ?? []
  )
}

async function request<T>(resourcePath: string, options: PortalRequestOptions = {}): Promise<T> {
  const { method = "GET", json, formData, sessionToken, responseMode = "json", signal } = options

  const headers = new Headers()
  headers.set("Accept", responseMode === "text" ? "text/html" : "application/json")
  if (json !== undefined) headers.set("Content-Type", "application/json")
  if (sessionToken) headers.set("Authorization", `Bearer ${sessionToken}`)

  const response = await fetch(`${BASE_URL}${resourcePath}`, {
    method,
    headers,
    body: json === undefined ? formData : JSON.stringify(json),
    cache: "no-store",
    signal,
  })

  if (!response.ok) throw await buildError(response)
  if (responseMode === "text") return (await response.text()) as T
  return (await response.json()) as T
}

export { request as requestPublicForm }

const publicationPath = (token: string, path = "") => `intake_form_web_publications/${encodeURIComponent(token)}${path}`
const invitationPath = (token: string, path = "") => `intake_form_invitations/${encodeURIComponent(token)}${path}`

export function fetchPublicOutboundFormDemoHtml(token: string) {
  return request<string>(`outbound_form_demos/${encodeURIComponent(token)}`, { responseMode: "text" })
}

export function fetchPublicIntakeFormPublication(token: string) {
  return request<PublicIntakeFormPublication>(publicationPath(token))
}

export function fetchPublicIntakeFormPublicationHtml(token: string, versionId: string) {
  return request<string>(publicationPath(token, `/form?version_id=${encodeURIComponent(versionId)}`), {
    responseMode: "text",
  })
}

export function uploadPublicIntakeFormPublicationDocument({
  token,
  versionId,
  clientSubmissionId,
  fieldKey,
  file,
}: {
  token: string
  versionId: string
  clientSubmissionId: string
  fieldKey: string
  file: File
}) {
  const formData = new FormData()
  formData.append("file", file)
  formData.append("field_key", fieldKey)
  formData.append("version_id", versionId)
  formData.append("client_submission_id", clientSubmissionId)

  return request<PublicIntakeFormUpload>(publicationPath(token, "/uploads"), { method: "POST", formData })
}

export function createPublicIntakeFormPublicationSubmission({
  token,
  versionId,
  submission,
}: {
  token: string
  versionId: string
  submission: PublicIntakeFormSubmissionPayload
}) {
  return request<PublicIntakeFormSubmissionResult>(publicationPath(token, "/submissions"), {
    method: "POST",
    json: { version_id: versionId, submission },
  })
}

export function fetchPublicIntakeFormPublicationSchedulingAvailability({
  token,
  versionId,
  fieldKey,
}: {
  token: string
  versionId: string
  fieldKey: string
}) {
  return request<PublicIntakeFormSchedulingAvailability>(
    publicationPath(
      token,
      `/scheduling_availabilities/${encodeURIComponent(fieldKey)}?version_id=${encodeURIComponent(versionId)}`
    )
  )
}

export function fetchPublicIntakeFormPublicationBookingAttempt({
  token,
  attemptId,
  clientSubmissionId,
}: {
  token: string
  attemptId: string
  clientSubmissionId: string
}) {
  return request<PublicIntakeFormBookingAttemptStatus>(
    publicationPath(
      token,
      `/scheduling_booking_attempts/${encodeURIComponent(attemptId)}?client_submission_id=${encodeURIComponent(clientSubmissionId)}`
    )
  )
}

export function fetchPublicIntakeFormInvitation(token: string, sessionToken: string | null) {
  return request<PublicIntakeFormInvitation>(invitationPath(token), { sessionToken })
}

export function requestPublicIntakeFormOtp(token: string, recipientToken?: string) {
  return request<{ message: string; masked_recipient_email: string; development_otp?: string }>(
    invitationPath(token, "/otp"),
    {
      method: "POST",
      json: { recipient_token: recipientToken },
    }
  )
}

export function verifyPublicIntakeFormOtp(token: string, otp: string, recipientToken?: string) {
  return request<PublicIntakeFormSessionToken>(invitationPath(token, "/verify"), {
    method: "POST",
    json: { otp, recipient_token: recipientToken },
  })
}

export function fetchPublicIntakeFormInvitationHtml(token: string, sessionToken: string) {
  return request<string>(invitationPath(token, "/form"), { responseMode: "text", sessionToken })
}

export async function fetchPublicIntakeFormInvitationDraft(token: string, sessionToken: string, signal?: AbortSignal) {
  return fetchPublicFormDraft(invitationPath(token, "/draft_submission"), sessionToken, signal)
}

export async function fetchPublicFormDraft(path: string, sessionToken: string, signal?: AbortSignal) {
  const key = invitationDraftSaveKey(path, sessionToken)
  const state = draftRequestState(key)
  state.activeReadCount += 1

  try {
    while (true) {
      if (state.failedSaves.length > 0) {
        await enqueueDraftOperation(key, state, async () => undefined)
      }

      const revision = state.revision
      const saveTail = state.saveTail
      await saveTail
      if (revision !== state.revision || saveTail !== state.saveTail) continue

      const draft = await request<PublicIntakeFormDraft | null>(path, {
        sessionToken,
        signal,
      })
      if (revision === state.revision) return draft
    }
  } finally {
    state.activeReadCount -= 1
    clearDraftRequestState(key, state)
  }
}

export function recordPublicIntakeFormInvitationEditing(token: string, sessionToken: string) {
  return request<{ active: true }>(invitationPath(token, "/editing_session"), { method: "POST", sessionToken })
}

export function savePublicIntakeFormInvitationDraft({
  token,
  sessionToken,
  draft,
}: {
  token: string
  sessionToken: string
  draft: PublicIntakeFormDraftPayload
}) {
  return savePublicFormDraft(invitationPath(token, "/draft_submission"), sessionToken, draft)
}

export function savePublicFormDraft(path: string, sessionToken: string, draft: PublicIntakeFormDraftPayload) {
  const saveKey = invitationDraftSaveKey(path, sessionToken)
  const state = draftRequestState(saveKey)
  const save = () =>
    request<PublicIntakeFormDraft>(path, {
      method: draft.id ? "PATCH" : "POST",
      sessionToken,
      json: {
        draft_submission: {
          client_submission_id: draft.client_submission_id,
          answers: draft.answers,
          documents: draft.documents,
          draft_state: {
            attestations: draft.attestations,
            touched_keys: draft.touched_keys,
            nav: draft.nav,
          },
        },
      },
    })
  return enqueueDraftOperation(saveKey, state, save, save)
}

export function uploadPublicIntakeFormInvitationDocument({
  token,
  sessionToken,
  clientSubmissionId,
  fieldKey,
  file,
}: {
  token: string
  sessionToken: string
  clientSubmissionId: string
  fieldKey: string
  file: File
}) {
  const formData = new FormData()
  formData.append("file", file)
  formData.append("field_key", fieldKey)
  formData.append("client_submission_id", clientSubmissionId)

  return request<PublicIntakeFormUpload>(invitationPath(token, "/uploads"), { method: "POST", formData, sessionToken })
}

export function createPublicIntakeFormInvitationSubmission({
  token,
  sessionToken,
  submission,
}: {
  token: string
  sessionToken: string
  submission: PublicIntakeFormSubmissionPayload
}) {
  return request<PublicIntakeFormSubmissionResult>(invitationPath(token, "/submissions"), {
    method: "POST",
    json: { submission },
    sessionToken,
  })
}

export function fetchPublicIntakeFormInvitationSchedulingAvailability({
  token,
  sessionToken,
  fieldKey,
}: {
  token: string
  sessionToken: string
  fieldKey: string
}) {
  return request<PublicIntakeFormSchedulingAvailability>(
    invitationPath(token, `/scheduling_availabilities/${encodeURIComponent(fieldKey)}`),
    { sessionToken }
  )
}

export function fetchPublicIntakeFormInvitationBookingAttempt({
  token,
  sessionToken,
  attemptId,
}: {
  token: string
  sessionToken: string
  attemptId: string
}) {
  return request<PublicIntakeFormBookingAttemptStatus>(
    invitationPath(token, `/scheduling_booking_attempts/${encodeURIComponent(attemptId)}`),
    { sessionToken }
  )
}
